// Libs
import classNames from 'classnames/bind';
import { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';
import { Box, Button, Stack } from '@mui/material';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ChangeEvent, useEffect, useMemo, useState } from 'react';
// Components, Layouts, Pages
import {
  BaseInputSearch,
  BaseMoreAction,
  BaseTable,
  BaseToolbar,
  ConfirmModal,
  Loading,
  SortComponent,
} from '~/components/common';
// Others
import useDebounce from '~/utils/hooks/useDebounce';
import {
  DEFAULT_CURRENT_PAGE,
  DEFAULT_DEBOUNCE_DELAY_TIME,
  DEFAULT_LIMIT_PAGE,
  DEFAULT_SORT_ROLE,
  EMPTY_STRING,
  EMPTY_VALUE,
} from '~/utils/constants/common';
import { IRole } from '~/utils/interface/role';
import { RoleEnum } from '~/utils/enum/common';
import { StorageEnum } from '~/utils/enum/base';
import { roleActions } from '~/thunks/role/roleSlice';
import { adminRouteAbsolute } from '~/utils/routes/roleRoutes/admin';
import { IPaginationResponse } from '~/utils/interface/base';
import { deleteRole, getListRole } from '~/thunks/role/roleThunk';
import { useAppDispatch, useAppSelector } from '~/utils/redux/hooks';
import { IBaseMoreAction, IHeaderCellType, IQueryParamBase, ISortBy } from '~/utils/interface/common';
import { crewRouteAbsolute } from '~/utils/routes/roleRoutes/crew';
import { loaderRouteAbsolute } from '~/utils/routes/roleRoutes/loader';
import { foremanRouteAbsolute } from '~/utils/routes/roleRoutes/foreman';
import { salesAdminRouteAbsolute } from '~/utils/routes/roleRoutes/salesAdmin';
import { salesPersonRouteAbsolute } from '~/utils/routes/roleRoutes/salesPerson';
import { warehouseLeadRouteAbsolute } from '~/utils/routes/roleRoutes/warehouseLead';
import { warehouseStaffRouteAbsolute } from '~/utils/routes/roleRoutes/warehouseStaff';
import { operationsManagerRouteAbsolute } from '~/utils/routes/roleRoutes/operationsManager';
import { usePermissions } from '~/utils/hooks/userPermissions';
import { ROLE_PERMISSIONS } from '~/utils/constants/permissions';
// Styles, images, icons
import styles from './RoleManagement.module.scss';
import { icons } from '~/assets';

type Props = {};

const cx = classNames.bind(styles);

const columnsRole = (
  t: TFunction,
  sortBy: ISortBy[],
  handleSort: (sortType: string, name: string) => void,
  onEdit: (id: string) => void,
  onDelete: (id: string) => void,
  isEdit: boolean,
  isDelete: boolean
): IHeaderCellType<IRole>[] => {
  const getSortType = (name: string) => sortBy.find((item) => item.name === name)?.typeSort || EMPTY_STRING;

  return [
    {
      id: 'id',
      label: (
        <Stack direction={'row'} alignItems={'center'} gap={2}>
          <Box>{t('table_header_title_id')}</Box>
          <SortComponent name='id' sortType={getSortType('id')} handleSort={handleSort} />
        </Stack>
      ),
      render: (record: IRole) => record.id || EMPTY_VALUE,
    },
    {
      id: 'name',
      label: (
        <Stack direction={'row'} alignItems={'center'} gap={2}>
          <Box>{t('table_header_title_role_name')}</Box>
          <SortComponent name='name' sortType={getSortType('name')} handleSort={handleSort} />
        </Stack>
      ),
      render: (record: IRole) => record?.name || EMPTY_VALUE,
    },
    {
      id: 'users',
      label: (
        <Stack direction={'row'} alignItems={'center'} gap={2}>
          <Box>{t('table_header_title_users')}</Box>
          <SortComponent name='users' sortType={getSortType('users')} handleSort={handleSort} />
        </Stack>
      ),
      render: (record: IRole) => record.users || EMPTY_VALUE,
    },
    {
      id: 'action',
      label: t('common_text_action', { ns: 'common/base' }),
      minWidth: 100,
      align: 'center',
      render: (record: IRole) => {
        const actions: IBaseMoreAction[] = [
          ...(isEdit
            ? [
                {
                  label: t('table_action_edit', { ns: 'common/base' }),
                  icon: (
                    <img
                      src={icons.tableIconActionEdit}
                      alt={t('common_alt_img', { ns: 'common/base' })}
                      width={16}
                      height={16}
                    />
                  ),
                  onClick: () => onEdit(record.id),
                },
              ]
            : []),
          ...(Number(record?.users) === 0 && isDelete
            ? [
                {
                  label: t('table_action_delete', { ns: 'common/base' }),
                  icon: (
                    <img
                      src={icons.tableIconActionDelete}
                      alt={t('common_alt_img', { ns: 'common/base' })}
                      width={16}
                      height={16}
                    />
                  ),
                  hasOtherColor: true,
                  onClick: () => onDelete(record.id),
                },
              ]
            : []),
        ];

        return <BaseMoreAction actions={actions} />;
      },
    },
  ];
};

const RoleManagement = (props: Props) => {
  //#region Destructuring Props
  const {} = props;

  const { canCreateRoles, canViewRoles, canEditRoles, canDeleteRoles } = usePermissions(ROLE_PERMISSIONS);
  //#endregion Destructuring Props

  //#region Declare Hook
  const { t } = useTranslation(['admin/role', 'common/base']);
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { isRefreshRoleList } = useAppSelector((state) => state.role);

  const [searchParams, setSearchParams] = useSearchParams();
  const params = useMemo(() => Object.fromEntries([...searchParams]), [searchParams]);
  const pageSelected = useMemo<number>(() => Number(params?.page ?? DEFAULT_CURRENT_PAGE), [params?.page]);
  const limitPage = useMemo<number>(() => Number(params?.limit ?? DEFAULT_LIMIT_PAGE), [params?.limit]);

  const currentRole = JSON.parse(localStorage.getItem(StorageEnum.ROLE)!)[0];
  //#endregion Declare Hook

  //#region Selector
  //#endregion Selector

  //#region Declare State
  const [sortBy, setSortBy] = useState<ISortBy[]>(DEFAULT_SORT_ROLE);
  const [searchKey, setSearchKey] = useState<string>(EMPTY_STRING);
  const debounceSearchKey = useDebounce(searchKey, DEFAULT_DEBOUNCE_DELAY_TIME);

  const [paramObject, setParamObject] = useState<IQueryParamBase>({
    page: pageSelected,
    limit: limitPage,
  });
  const [pagination, setPagination] = useState<IPaginationResponse>();

  const [roleList, setRoleList] = useState<IRole[]>([]);
  const [totalItems, setTotalItems] = useState<number>(0);

  const [idRole, setIdRole] = useState<string>(EMPTY_STRING);

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isShowConfirmDelete, setIsShowConfirmDelete] = useState<boolean>(false);
  //#endregion Declare State

  //#region Declare Memo
  //#endregion Declare Memo

  //#region Implement Hook
  useEffect(() => {
    handleGetListRole(paramObject);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paramObject]);

  useEffect(() => {
    if (!isRefreshRoleList) return;

    handleGetListRole(paramObject);

    dispatch(roleActions.setRefreshRoleList(false));

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isRefreshRoleList]);

  useEffect(() => {
    if (pagination || debounceSearchKey) {
      setParamObject({
        ...paramObject,
        page: DEFAULT_CURRENT_PAGE,
        limit: limitPage,
        ...(debounceSearchKey ? { textSearch: debounceSearchKey } : { textSearch: undefined }),
      });

      setSearchParams({
        page: String(DEFAULT_CURRENT_PAGE),
        limit: String(limitPage),
        ...(searchKey ? { textSearch: debounceSearchKey } : {}),
      });
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debounceSearchKey]);
  //#endregion Implement Hook

  //#region Handle Function
  const handleGetListRole = (params: IQueryParamBase) => {
    setIsLoading(true);

    dispatch(getListRole(params))
      .unwrap()
      .then((res) => {
        const { responses, pagination, totalItems } = res?.data;

        if (!responses) return;

        setRoleList(responses);
        setPagination(pagination);

        setTotalItems(totalItems);
      })
      .catch((_error) => {})
      .finally(() => {
        setIsLoading(false);
      });
  };

  const handleAdd = () => {
    switch (currentRole) {
      case RoleEnum.SUPER_ADMIN:
        navigate(adminRouteAbsolute.createRole);
        break;
      case RoleEnum.SALES_ADMIN:
        navigate(salesAdminRouteAbsolute.createRole);
        break;
      case RoleEnum.SALES_PERSON:
        navigate(salesPersonRouteAbsolute.createRole);
        break;
      case RoleEnum.OPERATIONS_MANAGER:
        navigate(operationsManagerRouteAbsolute.createRole);
        break;
      case RoleEnum.WAREHOUSE_LEAD:
        navigate(warehouseLeadRouteAbsolute.createRole);
        break;
      case RoleEnum.WAREHOUSE_STAFF:
        navigate(warehouseStaffRouteAbsolute.createRole);
        break;
      case RoleEnum.FOREMAN:
        navigate(foremanRouteAbsolute.createRole);
        break;
      case RoleEnum.CREW:
        navigate(crewRouteAbsolute.createRole);
        break;
      case RoleEnum.LOADER:
        navigate(loaderRouteAbsolute.createRole);
        break;
    }
  };

  const handleEdit = (id: string) => {
    switch (currentRole) {
      case RoleEnum.SUPER_ADMIN:
        navigate(adminRouteAbsolute.updateRole(id));
        break;
      case RoleEnum.SALES_ADMIN:
        navigate(salesAdminRouteAbsolute.updateRole(id));
        break;
      case RoleEnum.SALES_PERSON:
        navigate(salesPersonRouteAbsolute.updateRole(id));
        break;
      case RoleEnum.OPERATIONS_MANAGER:
        navigate(operationsManagerRouteAbsolute.updateRole(id));
        break;
      case RoleEnum.WAREHOUSE_LEAD:
        navigate(warehouseLeadRouteAbsolute.updateRole(id));
        break;
      case RoleEnum.WAREHOUSE_STAFF:
        navigate(warehouseStaffRouteAbsolute.updateRole(id));
        break;
      case RoleEnum.FOREMAN:
        navigate(foremanRouteAbsolute.updateRole(id));
        break;
      case RoleEnum.CREW:
        navigate(crewRouteAbsolute.updateRole(id));
        break;
      case RoleEnum.LOADER:
        navigate(loaderRouteAbsolute.updateRole(id));
        break;
        break;
    }
  };

  const handleDelete = (id: string) => {
    if (!id) return;

    setIdRole(id);
    setIsShowConfirmDelete(true);
  };

  const handleCloseConfirmDelete = () => {
    setIdRole(EMPTY_STRING);
    setIsShowConfirmDelete(false);
  };

  const handleConfirmDelete = () => {
    setIsLoading(true);

    dispatch(deleteRole(idRole))
      .unwrap()
      .then(() => {
        setIdRole(EMPTY_STRING);
        handleCloseConfirmDelete();
      })
      .catch(() => {})
      .finally(() => {
        setIsLoading(false);
      });
  };

  const handleSearchChange = (value: string) => {
    setSearchKey(value);
  };

  const handleSort = (sortType: string, name: string) => {
    const updatedSortBy = sortBy.map((item) => ({
      ...item,
      typeSort: item.name === name ? sortType : undefined,
    }));
    setSortBy(updatedSortBy);
    setParamObject({ ...paramObject, sortBy: name, sortType: sortType });
    setSearchParams({ ...params, sortBy: name, sortType: sortType });
  };

  const handlePageChange = (_event: ChangeEvent<unknown>, page: number) => {
    setParamObject({ ...paramObject, page });
    setSearchParams({ ...params, page: String(page), limit: String(limitPage) });
  };
  //#endregion Handle Function

  return (
    <Box id='roleManagementPage' className={cx('container')}>
      <BaseToolbar title={t('role_title_management')} isBack>
        <BaseInputSearch
          width={200}
          typeStyle='outlined'
          onChange={handleSearchChange}
          borderRadius={100}
          placeholder={t('common_text_search', { ns: 'common/base' })}
        />

        {canCreateRoles && (
          <Box className={cx('button')}>
            <Button
              type='button'
              variant='contained'
              className={cx('buttonAction')}
              onClick={handleAdd}
              startIcon={<img src={icons.commonIconPlus} alt={'common_alt_img'} />}
            >
              {t('role_text_add_role')}
            </Button>
          </Box>
        )}
      </BaseToolbar>

      <Box className={cx('body')}>
        <BaseTable
          dataSource={canViewRoles ? roleList : []}
          currentPage={pageSelected}
          totalPage={pagination?.totalPages}
          columns={columnsRole(t, sortBy, handleSort, handleEdit, handleDelete, canEditRoles, canDeleteRoles)}
          onPageChange={handlePageChange}
          {...(canEditRoles ? { onRowClick: handleEdit } : {})}
        />
      </Box>

      {isShowConfirmDelete && (
        <ConfirmModal
          type='delete'
          icon={icons.tableIconActionDelete}
          title={t('common_title_confirm_delete', { ns: 'common/base' })}
          textConfirm={t('common_action_delete', { ns: 'common/base' })}
          onClose={handleCloseConfirmDelete}
          onConfirm={handleConfirmDelete}
        />
      )}

      {isLoading && <Loading />}
    </Box>
  );
};

export default RoleManagement;
